import { Editor } from '@tiptap/react'
import React, { useEffect, useState } from 'react'
import SpeechRecognition, {
	useSpeechRecognition,
} from 'react-speech-recognition'

import { CircularProgress } from '@mui/material'
import classNames from 'classnames'

import AudioTranscriptionIcon from 'assets/images/AudioTranscription.svg'
import AudioTranscriptionRecordingIcon from 'assets/images/AudioTranscriptionRecording.svg'

import './styles.css'

export interface Props {
	editor: Editor | null
	editorId: string
}

// Scoped variable to track active editor
let activeEditorId: string | null = null
export const setActiveEditorId = (id: string | null) => {
	activeEditorId = id
}

const AudioTranscriptionButton: React.FC<Props> = ({ editor, editorId }) => {
	const { interimTranscript, resetTranscript } = useSpeechRecognition()
	const [isTranscribing, setIsTranscribing] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [lastTranscript, setLastTranscript] = useState('')

	useEffect(() => {
		if (editorId !== activeEditorId) return
		if (!interimTranscript || !editor) return

		const newText = interimTranscript.slice(lastTranscript.length)
		if (newText) {
			editor.chain().focus().insertContent(newText).run()
			setLastTranscript(interimTranscript)
		}
	}, [interimTranscript, editorId, editor, lastTranscript])

	const handleListen = () => {
		if (!editor) return

		if (isTranscribing) {
			SpeechRecognition.stopListening()
			setIsTranscribing(false)
			resetTranscript()
			setLastTranscript('')
			activeEditorId = null
		} else {
			resetTranscript()
			setLastTranscript('')
			activeEditorId = editorId
			setIsLoading(true)
			SpeechRecognition.startListening({
				continuous: true,
				interimResults: true,
			})
			setTimeout(() => {
				setIsLoading(false)
				setIsTranscribing(true)
			}, 1000)
		}
	}

	const isSpeechSupported =
		typeof window !== 'undefined' &&
		('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)

	if (!isSpeechSupported || !editor) {
		return (
			<span data-testid="audio-transcription-not-supported">
				Browser does not support speech recognition.
			</span>
		)
	}

	return (
		<div className="audio-transcription-wrapper">
			{isLoading ? (
				<CircularProgress size={15} sx={{ marginLeft: '1rem' }} />
			) : (
				<button
					className={classNames('menu-button', {
						'is-active': isTranscribing,
					})}
					onClick={handleListen}
					aria-label="audio-transcription-icon"
					data-testid="audio-transcription-icon"
				>
					<img
						src={
							isTranscribing
								? (AudioTranscriptionRecordingIcon as unknown as string)
								: (AudioTranscriptionIcon as unknown as string)
						}
						alt="transcription icon"
					/>
				</button>
			)}
			{isTranscribing && (
				<span className="transcribing-text">Transcribing...</span>
			)}
		</div>
	)
}

export default AudioTranscriptionButton
